import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { FaUsers, FaChartLine, FaCalendarDay } from 'react-icons/fa';
import './TrafficWidget.css';

const TrafficWidget = () => {
    const [totalVisits, setTotalVisits] = useState(0);
    const [todayVisits, setTodayVisits] = useState(0);
    const [weeklyStats, setWeeklyStats] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // 1. Listen to Global Stats
        const globalUnsub = onSnapshot(doc(db, 'visitor_stats', 'global'), (doc) => {
            if (doc.exists()) {
                setTotalVisits(doc.data().totalVisits || 0);
            }
        });

        // 2. Listen to Today's Stats
        const today = new Date().toISOString().split('T')[0];
        const dailyUnsub = onSnapshot(doc(db, 'visitor_stats', today), (doc) => {
            if (doc.exists()) {
                setTodayVisits(doc.data().count || 0);
            } else {
                setTodayVisits(0);
            }
        });

        // 3. Fetch Last 7 Days (One-time fetch for now, can be real-time if needed)
        // Note: For a true 7-day trend, we might need to structured queries properly.
        // For now, we will try to fetch the last 7 documents from visitor_stats collection.
        // A better approach in production is to have a dedicated 'daily_stats' collection or similar.
        // Assuming 'visitor_stats' contains YYYY-MM-DD documents.

        const fetchWeeklyStats = async () => {
            // Since visitor_stats also has 'global', we need to be careful.
            // But we can just query by ID pattern or just grab all and filter.
            // However, Firestore doesn't support regex on IDs easily.
            // We'll rely on a client-side filter for now for simplicity or specific date queries.

            // Better: Generate last 7 dates and fetch them.
            const dates = [];
            for (let i = 6; i >= 0; i--) {
                const d = new Date();
                d.setDate(d.getDate() - i);
                dates.push(d.toISOString().split('T')[0]);
            }


            // Actually, getDoc is better for known IDs.

            // Optimised: just try to get specific docs
            // But we can't do where('__name__', 'in', dates) because max 10, which is fine for 7.
            // Let's try to query collection if possible, but IDs are mixed.
            // Let's just do individual fetches in parallel, it's fast enough for 7 docs.

            // Re-evaluating: 'visitor_stats' root collection has 'global' and dates.
            // Let's just fetch all mostly, or limit.
            // But 'global' is there.

            // Let's use the exact IDs approach
            // We need to import getDoc inside here or reuse the logic.
            // But we are using onSnapshot for listeners.

            // Let's stick to fetch for the chart history.
            try {
                // Actually, let's just make a helper
                // We will build an array of {date, count}
                const statsPromises = dates.map(async (date) => {
                    const dRef = doc(db, 'visitor_stats', date);
                    const dSnap = await import('firebase/firestore').then(mod => mod.getDoc(dRef));
                    return {
                        date,
                        count: dSnap.exists() ? dSnap.data().count : 0
                    };
                });

                const results = await Promise.all(statsPromises);
                setWeeklyStats(results);
                setLoading(false);
            } catch (err) {
                console.error("Error fetching weekly stats", err);
                setLoading(false);
            }
        };

        fetchWeeklyStats();

        return () => {
            globalUnsub();
            dailyUnsub();
        };
    }, []);

    // Simple max value for chart scaling
    const maxCount = Math.max(...weeklyStats.map(s => s.count), 1);

    return (
        <div className="traffic-widget">
            <h3 className="traffic-title"><FaChartLine /> Traffic Pengunjung</h3>

            <div className="traffic-stats-row">
                <div className="traffic-stat-item total">
                    <div className="traffic-icon"><FaUsers /></div>
                    <div className="traffic-info">
                        <span className="traffic-label">Total Kunjungan</span>
                        <span className="traffic-value">{totalVisits.toLocaleString()}</span>
                    </div>
                </div>

                <div className="traffic-stat-item today">
                    <div className="traffic-icon"><FaCalendarDay /></div>
                    <div className="traffic-info">
                        <span className="traffic-label">Hari Ini</span>
                        <span className="traffic-value">{todayVisits.toLocaleString()}</span>
                    </div>
                </div>
            </div>

            <div className="traffic-chart-container">
                <h4>Tren 7 Hari Terakhir</h4>
                <div className="traffic-chart">
                    {loading ? (
                        <div className="traffic-loading">Memuat data...</div>
                    ) : (
                        <div className="chart-bars">
                            {weeklyStats.map((stat, index) => {
                                const heightPercent = (stat.count / maxCount) * 100;
                                // Format date: DD/MM
                                const [, month, day] = stat.date.split('-');
                                const dateLabel = `${day}/${month}`;

                                return (
                                    <div key={stat.date} className="chart-bar-wrapper" title={`${stat.date}: ${stat.count}`}>
                                        <div
                                            className="chart-bar"
                                            style={{ height: `${Math.max(heightPercent, 5)}%` }} // min height 5%
                                        >
                                            <span className="chart-bar-tooltip">{stat.count}</span>
                                        </div>
                                        <span className="chart-bar-label">{dateLabel}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TrafficWidget;
